import React, { useState } from "react";
import { toast } from "react-toastify";
import { ArrowDown, ArrowUp, Lock, Pencil, Trash2 } from "lucide-react";
import { PrimaryButton, SecondaryButton } from "../../UI";
import PrimaryModal from "../../UI/Modal/PrimaryModal";
import { Field, IconButton, InfoTip, JsonEditor, LinkButton, NumberInput, optionsTip, SelectInput, StatusMessage, TextInput, useStatus, WithInfo } from "./common";
import { COLUMN_KIND_LABELS, COLUMN_PURPOSES, COLUMN_VARIANT_HELP, COLUMN_VARIANT_LABELS, PRICE_RULE_HELP, PRICE_RULE_LABELS, ROLE_LABELS } from "./constants";
import { explain, GLOSSARY } from "./glossary";
import { moveItem } from "./helpers";
import { TransformEditor } from "./source/ExpressionEditor";

const bare = (name) => String(name || "").replace(/^entity\./, "");

const describeColumn = (column) => {
    const ref = column.field || (column.role ? `${ROLE_LABELS[column.role] || column.role} role` : "");
    switch (column.kind) {
        case "product":
            return ref;
        case "representative":
            return `${column.variant === "price" ? "price variant" : "representative"} ${ref}`;
        case "aggregate":
            return `${column.op}${ref ? ` of ${ref}` : ""}${column.where && column.where !== "all" ? ` where ${typeof column.where === "string" ? column.where : "…"}` : ""}`;
        case "list":
            return `list of ${ref}${column.null_as !== undefined ? ` (blank → ${column.null_as})` : ""}`;
        case "indexed":
            return `${ref} in ${column.count || 1} columns`;
        case "variants_json":
            return `JSON of ${(column.fields || []).map((f) => (typeof f === "string" ? f : f.name || f.field)).join(", ")}`;
        case "template":
            return column.template;
        case "const":
            return column.value === null || column.value === undefined ? "blank" : `"${column.value}"`;
        case "fallback":
            return (column.of || []).map(describeColumn).join(" → ");
        default:
            return "";
    }
};

const KIND_TIP = optionsTip("How the column gets its value.", GLOSSARY.columns.kinds, COLUMN_KIND_LABELS);
const VARIANT_FILTER_TIP = optionsTip("Which of the product's variants the column uses.", GLOSSARY.variantFilters);
const ON_LIMIT_TIP = optionsTip("Target limits list values to 100 characters and 500 values, and single values to 15,000 characters (5,000 for some scripts).", GLOSSARY.columns.onLimit);
const PRICE_RULE_TIP = optionsTip("entity.value is locked to one of these rules.", PRICE_RULE_HELP, PRICE_RULE_LABELS);

const countCustom = (columns, reserved) =>
    columns.reduce((sum, c) => (reserved.includes(bare(c.name)) ? sum : sum + (c.kind === "indexed" ? Math.max(1, Number(c.count) || 1) : 1)), 0);

// Field-or-role picker used by several column kinds.
const RefPicker = ({ column, onChange, fields, roles }) => (
    <Field label="Value from" tip={GLOSSARY.columns.valueFrom}>
        <SelectInput
            value={column.field ? `field:${column.field}` : column.role ? `role:${column.role}` : ""}
            placeholder="Choose…"
            options={[
                { value: "role:key", label: "Key" },
                ...roles.filter((r) => r !== "key").map((r) => ({ value: `role:${r}`, label: `${ROLE_LABELS[r] || r} role` })),
                ...fields.map((f) => ({ value: `field:${f.key}`, label: `Field: ${f.key}` })),
                { value: "field:$available", label: "Available (true/false)" },
            ]}
            onChange={(value) => {
                const [kind, name] = value.split(":");
                const next = { ...column };
                delete next.field;
                delete next.role;
                if (name) next[kind] = name;
                onChange(next);
            }}
        />
    </Field>
);

const ColumnEditor = ({ column, onSave, onClose, model, meta, reserved, takenNames }) => {
    const [draftColumn, setDraftColumn] = useState(column);
    const [status, showStatus] = useStatus();

    const save = () => {
        const name = bare(draftColumn.name);
        if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) {
            showStatus("Column names start with a letter and use only letters, digits and _.", "error");
            return;
        }
        if (takenNames.includes(name)) {
            showStatus(`There's already a column called entity.${name}. Edit that one or choose another name.`, "error");
            return;
        }
        onSave({ ...draftColumn, name });
    };
    const set = (patch) => setDraftColumn({ ...draftColumn, ...patch });
    const isReserved = reserved.includes(bare(draftColumn.name));
    const productFields = model.product_fields || [];
    const variantFields = model.variant_fields || [];
    const productRoles = [...new Set(productFields.map((f) => f.role).filter(Boolean))];
    const variantRoles = [...new Set(variantFields.map((f) => f.role).filter(Boolean))];
    const kind = draftColumn.kind;

    return (
        <PrimaryModal title={`Column: entity.${bare(draftColumn.name) || "…"}`} hideModal={onClose} widthClass="max-w-3xl">
            <div className="p-6 flex flex-col gap-4 max-h-[75vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field
                        label="Name"
                        help={isReserved ? "Reserved Target attribute" : "Letters, digits and _; written as entity.<name>"}
                        tip={isReserved ? GLOSSARY.columns.reservedColumns[bare(draftColumn.name)] : GLOSSARY.columns.custom}
                    >
                        <TextInput monospace disabled={isReserved} value={bare(draftColumn.name)} onChange={(name) => set({ name: name.replace(/[^A-Za-z0-9_]/g, "") })} />
                    </Field>
                    <Field label="Kind" tip={KIND_TIP}>
                        <SelectInput value={kind} options={(meta?.column_kinds || Object.keys(COLUMN_KIND_LABELS)).filter((k) => !(isReserved && k === "indexed")).map((k) => ({ value: k, label: COLUMN_KIND_LABELS[k] || k }))} onChange={(value) => set({ kind: value })} />
                    </Field>
                </div>

                {kind === "product" && <RefPicker column={draftColumn} onChange={setDraftColumn} fields={productFields} roles={productRoles} />}
                {["list", "indexed"].includes(kind) && <RefPicker column={draftColumn} onChange={setDraftColumn} fields={variantFields} roles={variantRoles} />}
                {kind === "representative" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <RefPicker column={draftColumn} onChange={setDraftColumn} fields={variantFields} roles={variantRoles} />
                        <Field label="From variant" help={COLUMN_VARIANT_HELP[draftColumn.variant || "representative"]} tip={optionsTip("Which variant this column reads.", COLUMN_VARIANT_HELP, COLUMN_VARIANT_LABELS)}>
                            <SelectInput
                                value={draftColumn.variant || "representative"}
                                options={(meta?.column_variants || Object.keys(COLUMN_VARIANT_LABELS)).map((v) => ({ value: v, label: COLUMN_VARIANT_LABELS[v] || v }))}
                                onChange={(variant) => set({ variant: variant === "representative" ? undefined : variant })}
                            />
                        </Field>
                    </div>
                )}
                {kind === "aggregate" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Operation" tip={optionsTip("How the variants' values become one value.", GLOSSARY.columns.aggregateOps)}>
                            <SelectInput value={draftColumn.op} placeholder="Choose…" options={meta?.aggregate_ops || []} onChange={(op) => set({ op })} />
                        </Field>
                        {draftColumn.op !== "count" && <RefPicker column={draftColumn} onChange={setDraftColumn} fields={variantFields} roles={variantRoles} />}
                    </div>
                )}
                {["aggregate", "list", "indexed", "variants_json"].includes(kind) && (
                    <Field label="Variants" help="Custom conditions can be set in the JSON below." tip={VARIANT_FILTER_TIP}>
                        <SelectInput
                            value={typeof draftColumn.where === "object" ? "custom" : draftColumn.where || "all"}
                            options={[
                                { value: "all", label: "All variants" },
                                { value: "available", label: "Available variants" },
                                { value: "unavailable", label: "Unavailable variants" },
                                ...(typeof draftColumn.where === "object" ? [{ value: "custom", label: "Custom condition" }] : []),
                            ]}
                            onChange={(where) => where !== "custom" && set({ where: where === "all" ? undefined : where })}
                        />
                    </Field>
                )}
                {kind === "list" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Blank values become" help="Target rejects lists that mix numbers and blanks" tip={GLOSSARY.columns.nullAs}>
                            <TextInput value={draftColumn.null_as ?? ""} onChange={(v) => set({ null_as: v === "" ? undefined : Number.isNaN(Number(v)) ? v : Number(v) })} />
                        </Field>
                        <Field label="Distinct values only" tip={GLOSSARY.columns.dedupe}>
                            <SelectInput value={draftColumn.dedupe ? "yes" : "no"} options={[{ value: "no", label: "No (one per variant)" }, { value: "yes", label: "Yes" }]} onChange={(v) => set({ dedupe: v === "yes" || undefined })} />
                        </Field>
                    </div>
                )}
                {kind === "indexed" && (
                    <Field label="Number of columns" help="Each column counts toward the 100 custom columns" tip={GLOSSARY.columns.count_slots}>
                        <NumberInput value={draftColumn.count} min={1} max={100} onChange={(count) => set({ count })} />
                    </Field>
                )}
                {kind === "variants_json" && (
                    <Field label="Variant fields" help="Output name = field. Use $available for 1/0 availability." tip={GLOSSARY.columns.jsonFields}>
                        <TextInput
                            monospace
                            value={(draftColumn.fields || []).map((f) => (typeof f === "string" ? f : f.name === f.field ? f.field : `${f.name}=${f.field}`)).join(", ")}
                            placeholder="color, price, img=image, live=$available"
                            onChange={(text) =>
                                set({
                                    fields: text.split(",").map((part) => part.trim()).filter(Boolean).map((part) => {
                                        const [name, field] = part.includes("=") ? part.split("=").map((p) => p.trim()) : [part, part];
                                        return { name, field };
                                    }),
                                })
                            }
                        />
                    </Field>
                )}
                {kind === "template" && (
                    <Field label="Template" help="{{$product.<field>}}, {{$product.key}}, {{$representative.<field>}}" tip={GLOSSARY.columns.kinds.template}>
                        <TextInput monospace value={draftColumn.template} onChange={(template) => set({ template })} />
                    </Field>
                )}
                {kind === "const" && (
                    <Field label="Value" help="Leave empty for a blank cell" tip={GLOSSARY.columns.constValue}>
                        <TextInput value={draftColumn.value ?? ""} onChange={(value) => set({ value: value === "" ? null : value })} />
                    </Field>
                )}
                {kind === "fallback" && (
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                        Edit the options in the JSON below: each is a column definition without a name.
                        <InfoTip content={GLOSSARY.columns.kinds.fallback} />
                    </p>
                )}
                {["list", "variants_json"].includes(kind) || !isReserved ? (
                    <Field label="When a value breaks Target's limits" tip={ON_LIMIT_TIP}>
                        <SelectInput
                            value={draftColumn.on_limit || (kind === "variants_json" ? "truncate_variants" : "shorten")}
                            options={kind === "variants_json"
                                ? [{ value: "drop_fields", label: "Drop fields from the end, then variants" }, { value: "truncate_variants", label: "Drop variants from the end" }]
                                : [{ value: "shorten", label: "Shorten" }, { value: "skip_value", label: "Skip the value" }, { value: "fail_row", label: "Drop the row" }]}
                            onChange={(on_limit) => set({ on_limit })}
                        />
                    </Field>
                ) : null}

                <Field label="Transforms" tip={GLOSSARY.columns.transforms}>
                    <TransformEditor catalogue={meta?.transforms || []} transforms={draftColumn.transforms || []} onChange={(transforms) => set({ transforms: transforms.length ? transforms : undefined })} />
                </Field>
                <Field label="Required" help="Rows where this column is empty are dropped and reported" tip={GLOSSARY.columns.required}>
                    <SelectInput value={draftColumn.required ? "yes" : "no"} options={[{ value: "no", label: "No" }, { value: "yes", label: "Yes" }]} onChange={(v) => set({ required: v === "yes" || undefined })} />
                </Field>

                <details>
                    <summary className="text-sm font-medium text-primary cursor-pointer">Edit as JSON</summary>
                    <JsonEditor rows={10} value={draftColumn} onChange={(v) => v && setDraftColumn(v)} className="mt-2" />
                </details>

                <div className="flex flex-wrap items-center justify-end gap-3">
                    <StatusMessage status={status} className="mr-auto" />
                    <SecondaryButton size="sm" handleClick={onClose}>Cancel</SecondaryButton>
                    <PrimaryButton size="sm" disable={!bare(draftColumn.name)} handleClick={save}>Save column</PrimaryButton>
                </div>
            </div>
        </PrimaryModal>
    );
};

// CSV columns tab.
const ColumnsTab = ({ draft, updateDraft, meta }) => {
    const [editing, setEditing] = useState(null);
    const [adding, setAdding] = useState(false);
    const [purpose, setPurpose] = useState(COLUMN_PURPOSES[0].id);
    const [showJson, setShowJson] = useState(false);
    const columns = draft.columns || [];
    const reserved = meta?.reserved_columns || ["id", "name", "categoryId", "message", "thumbnailUrl", "value", "pageUrl", "inventory", "margin"];
    const limit = meta?.limits?.maxCustomColumns || 100;
    const used = countCustom(columns, reserved);

    const setColumns = (next) => updateDraft((d) => ({ ...d, columns: next }));
    const priceRule = draft.settings?.price_rule || "lowest_available";

    // The locked entity.value column is rebuilt from the chosen rule (the server does the same).
    const setPriceRule = (rule) => {
        updateDraft((d) => ({
            ...d,
            settings: { ...d.settings, price_rule: rule },
            columns: (d.columns || []).map((c) => (c.locked && bare(c.name) === "value" && meta?.price_columns?.[rule] ? { ...meta.price_columns[rule] } : c)),
        }));
        toast.success(`entity.value now uses the ${(PRICE_RULE_LABELS[rule] || rule).toLowerCase()}. Save the draft to keep it.`);
    };
    const reservedColumns = reserved.map((name) => ({ name, column: columns.find((c) => bare(c.name) === name) }));
    const customColumns = columns.map((column, index) => ({ column, index })).filter(({ column }) => !reserved.includes(bare(column.name)));

    const saveColumn = (column) => {
        if (editing.index === undefined) setColumns([...columns, column]);
        else setColumns(columns.map((c, i) => (i === editing.index ? column : c)));
        toast.success(`entity.${bare(column.name)} ${editing.index === undefined ? "added" : "updated"}. Save the draft to keep it.`);
        setEditing(null);
    };

    const removeColumn = (index) => {
        setColumns(columns.filter((_, i) => i !== index));
        toast.info(`entity.${bare(columns[index]?.name)} removed. Save the draft to keep the change.`);
    };

    const moveCustom = (position, direction) => {
        const target = customColumns[position + direction];
        if (!target) return;
        setColumns(moveItem(columns, customColumns[position].index, target.index));
    };

    const row = (name, column, index, position) => {
        const locked = column?.locked;
        return (
            <tr key={name} className="border-b border-gray-100">
                <td className="py-2 pr-2 font-mono whitespace-nowrap">
                    <WithInfo tip={explain(GLOSSARY.columns.reservedColumns, name)}>entity.{name}</WithInfo>
                </td>
                <td className="py-2 pr-2 text-xs text-gray-600">{column ? COLUMN_KIND_LABELS[column.kind] || column.kind : "Blank"}</td>
                <td className="py-2 pr-2 text-xs font-mono text-gray-700 break-all">
                    {column ? describeColumn(column) : "—"}
                    {column?.transforms?.length > 0 && <span className="text-gray-400"> · {column.transforms.map((t) => t.op).join(" → ")}</span>}
                    {column?.required && <span className="ml-1 text-red-500">required</span>}
                </td>
                <td className="py-2 whitespace-nowrap text-right">
                    {locked ? (
                        <span className="inline-flex items-center gap-2 text-xs text-gray-500">
                            <Lock size={14} className="shrink-0" />
                            <SelectInput
                                value={priceRule}
                                options={(meta?.price_rules || Object.keys(PRICE_RULE_LABELS)).map((rule) => ({ value: rule, label: PRICE_RULE_LABELS[rule] || rule }))}
                                onChange={setPriceRule}
                                className="min-w-[210px]"
                            />
                            <InfoTip content={PRICE_RULE_TIP} wide />
                        </span>
                    ) : (
                        <>
                            {position !== undefined && (
                                <>
                                    <IconButton title="Move up" disabled={position === 0} onClick={() => moveCustom(position, -1)}><ArrowUp size={14} /></IconButton>
                                    <IconButton title="Move down" disabled={position === customColumns.length - 1} onClick={() => moveCustom(position, 1)}><ArrowDown size={14} /></IconButton>
                                </>
                            )}
                            <IconButton title="Edit" onClick={() => setEditing({ index, column: column || { name, kind: "product" } })}><Pencil size={14} /></IconButton>
                            {position !== undefined && (
                                <IconButton title="Remove" onClick={() => removeColumn(index)}><Trash2 size={14} /></IconButton>
                            )}
                        </>
                    )}
                </td>
            </tr>
        );
    };

    return (
        <div className="flex flex-col gap-4 mt-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-col gap-1 min-w-[260px]">
                    <span className="text-sm text-gray-700 flex items-center gap-1">
                        Custom columns: {used} / {limit}
                        <InfoTip content={GLOSSARY.columns.count} />
                    </span>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-2 ${used > limit ? "bg-red-500" : "bg-primary"}`} style={{ width: `${Math.min(100, (used / limit) * 100)}%` }} />
                    </div>
                </div>
                <PrimaryButton size="sm" handleClick={() => setAdding(true)}>Add column</PrimaryButton>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                            <th className="py-2 pr-2">Column</th>
                            <th className="py-2 pr-2"><WithInfo tip={KIND_TIP} wide>Kind</WithInfo></th>
                            <th className="py-2 pr-2">Value</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        <tr><td colSpan={4} className="pt-3 pb-1 text-xs font-semibold text-gray-500 uppercase"><WithInfo tip={GLOSSARY.columns.reserved}>Reserved</WithInfo></td></tr>
                        {reservedColumns.map(({ name, column }) => row(name, column, column ? columns.indexOf(column) : undefined))}
                        <tr><td colSpan={4} className="pt-4 pb-1 text-xs font-semibold text-gray-500 uppercase"><WithInfo tip={GLOSSARY.columns.custom}>Custom</WithInfo></td></tr>
                        {customColumns.length === 0 && <tr><td colSpan={4} className="py-2 text-sm text-gray-500">No custom columns yet.</td></tr>}
                        {customColumns.map(({ column, index }, position) => row(bare(column.name), column, index, position))}
                    </tbody>
                </table>
            </div>

            <p className="text-xs text-gray-500">
                Target limits: list values up to {meta?.limits?.maxListValueChars || 100} characters and {meta?.limits?.maxListValues || 500} values; single values up to{" "}
                {meta?.limits?.maxSingleValueChars || 15000} characters; categories up to {meta?.limits?.maxCategoryChars || 250} characters. Add Target's
                "inventory &gt; 0" inclusion rule so unavailable products aren't recommended.
            </p>

            <LinkButton onClick={() => setShowJson(!showJson)}>{showJson ? "Hide" : "Edit"} columns as JSON</LinkButton>
            {showJson && <JsonEditor rows={20} value={columns} onChange={(v) => Array.isArray(v) && setColumns(v)} />}

            {adding && (
                <PrimaryModal title="Add column" hideModal={() => setAdding(false)}>
                    <div className="p-6 flex flex-col gap-4">
                        <Field label="What is this column for?" tip={GLOSSARY.columns.purpose}>
                            <div className="flex flex-col gap-2">
                                {COLUMN_PURPOSES.map((p) => (
                                    <label key={p.id} className="flex items-center gap-2 text-sm">
                                        <input type="radio" name="purpose" checked={purpose === p.id} onChange={() => setPurpose(p.id)} />
                                        {p.label}
                                    </label>
                                ))}
                            </div>
                        </Field>
                        <div className="flex justify-end gap-3">
                            <SecondaryButton size="sm" handleClick={() => setAdding(false)}>Cancel</SecondaryButton>
                            <PrimaryButton
                                size="sm"
                                handleClick={() => {
                                    const preset = COLUMN_PURPOSES.find((p) => p.id === purpose);
                                    setAdding(false);
                                    setEditing({ index: undefined, column: { name: "", ...JSON.parse(JSON.stringify(preset.column)) } });
                                }}
                            >
                                Next
                            </PrimaryButton>
                        </div>
                    </div>
                </PrimaryModal>
            )}

            {editing && (
                <ColumnEditor
                    column={editing.column}
                    model={draft.model || {}}
                    meta={meta}
                    reserved={reserved}
                    takenNames={columns.filter((_, i) => i !== editing.index).map((c) => bare(c.name))}
                    onClose={() => setEditing(null)}
                    onSave={saveColumn}
                />
            )}
        </div>
    );
};

export default ColumnsTab;
